import { useCallback, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { registerLocale } from 'react-datepicker';
import { es, enGB, fr, pt, de } from 'date-fns/locale';
import { useLocation } from 'react-router-dom';
import * as Sentry from '@sentry/react';
import { AppNoInfo } from '@/layout/AppComponents';
import * as authActions from '@/store/actions/authAction';
import * as globalActions from '@/store/actions/globalAction';
import * as currentPageAction from '@/store/actions/currentPageAction';
import MainContainer from './MainContainer';
import PublicMainContainer from './PublicMainContainer';
import NavBar from './navBar/NavBar';
import ErrorBoundary from './components/error/ErrorBoundary';
import wlLogoD from '@/assets/images/worldlexnetD.png';

registerLocale('es', es);
registerLocale('enGB', enGB);
registerLocale('fr', fr);
registerLocale('pt', pt);
registerLocale('de', de);

const Layout = (props) => {
  const { t, i18n } = useTranslation();
  const location = useLocation();
  const dispatch = useDispatch();
  const global = useSelector((state) => state.global);
  const auth = useSelector((state) => state.auth);
  const recentlyLoggedIn = useSelector(
    (state) => state.currentPage?.recentlyLoggedIn,
  );
  const maintenanceMode = useSelector((state) => state.auth?.maintenance);

  const loadUser = useCallback(async () => {
    try {
      await dispatch(authActions.fetchAuthUser());
    } catch (err) {
      window.location.href = `${window.location.origin}/login/`;
    }
  }, [dispatch]);

  useEffect(() => {
    if (auth?.user?.id) {
      console.log('auth', auth);
      Sentry.setUser({
        id: auth?.user?.id,
        email: auth?.user?.email,
        username: auth?.user?.user,
      });
      Sentry.setTag('client', auth?.user?.client?.name);
    }
  }, [auth]);

  useEffect(() => {
    // Busca y guarda el idioma de usuario
    if (!localStorage.getItem('locale')) {
      const browserLocale = navigator.language.split('-')[0];
      localStorage.setItem('locale', browserLocale);
    }
    i18n.changeLanguage(localStorage.getItem('locale'));

    // Carga la info de parámetros
    dispatch(
      globalActions.setUrlData({
        rootProject: import.meta.env.VITE_APP_ROOT_PROJECT,
        baseUrl: import.meta.env.VITE_APP_BASE_URL,
        lang: localStorage.getItem('locale'),
        userApiUrl: import.meta.env.VITE_APP_ROOT_API,
        newLang: undefined,
      }),
    );
    // Si no es público, carga user al entrar en la web
    if (
      !location.pathname.includes('/login/') &&
      !location.pathname.includes('/o/')
    )
      loadUser();
  }, []);
  // Si viene del login, carga user
  useEffect(() => {
    if (recentlyLoggedIn === true) {
      dispatch(currentPageAction.empty());
      loadUser();
    }
  }, [recentlyLoggedIn]);

  // Gestión de variantes de idioma
  useEffect(() => {
    if (global.urlData) {
      if (
        global?.route?.params?.moduleBridgeHex &&
        auth?.languageVariants?.modules?.[
          global?.route?.params?.moduleBridgeHex
        ]?.[global.urlData.lang]
      ) {
        if (
          i18n.language !=
          auth?.languageVariants?.modules?.[
            global?.route?.params?.moduleBridgeHex
          ]?.[global.urlData.lang]
        ) {
          i18n.changeLanguage(
            auth?.languageVariants?.modules?.[
              global?.route?.params?.moduleBridgeHex
            ]?.[global.urlData.lang],
          );
        }
      } else if (auth?.languageVariants?.client?.[global.urlData.lang]) {
        if (
          i18n.language != auth?.languageVariants?.client?.[global.urlData.lang]
        ) {
          i18n.changeLanguage(
            auth?.languageVariants?.client?.[global.urlData.lang],
          );
        }
      } else if (i18n.language != global.urlData.lang) {
        i18n.changeLanguage(global.urlData.lang);
      }
    }
  }, [global?.route, global.urlData, auth]);

  if (
    location.pathname.includes('/login/') ||
    location.pathname.includes('/o/')
  ) {
    return <PublicMainContainer />;
  }
  if (maintenanceMode)
    return (
      <div
        style={{
          width: '100%',
          height: '100%',
          backgroundColor: '#f4f6f9',
          position: 'absolute',
          zIndex: 999999,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexDirection: 'column',
          gap: '100px',
        }}
      >
        <img
          src={wlLogoD}
          style={{
            width: '400px',
            height: 'auto',
          }}
        />
        <div
          style={{
            width: '800px',
            height: 'auto',
            backgroundColor: '#ffffff',
            borderRadius: '10px',
            padding: '30px',
            textAlign: 'center',
          }}
        >
          <p
            style={{
              fontSize: '18px',
              color: '#325a6b',
              marginBottom: '20px',
            }}
          >
            🔧 Mantenimiento programado
          </p>
          <p
            style={{
              fontSize: '18px',
              color: '#325a6b',
              marginBottom: '20px',
            }}
          >
            Nuestro sistema se encuentra temporalmente inactivo debido a tareas
            de mantenimiento programado. Estaremos de vuelta pronto para seguir
            ofreciéndote el mejor servicio.
          </p>
          <p
            style={{
              fontSize: '18px',
              color: '#325a6b',
              marginBottom: '20px',
            }}
          >
            Disculpa las molestias.
          </p>
          <hr />
          <p
            style={{
              fontSize: '18px',
              color: '#325a6b',
              marginBottom: '20px',
            }}
          >
            🔧 Scheduled Maintenance
          </p>
          <p
            style={{
              fontSize: '18px',
              color: '#325a6b',
              marginBottom: '20px',
            }}
          >
            Our system is temporarily unavailable due to scheduled maintenance.
            We’ll be back soon to continue providing you with the best service.
          </p>
          <p
            style={{
              fontSize: '18px',
              color: '#325a6b',
            }}
          >
            We apologize for the inconvenience.
          </p>
        </div>
      </div>
    );

  return (
    <div>
      {auth.user ? (
        <>
          <NavBar />
          <section className="container-fluid">
            <ErrorBoundary global={global} auth={auth}>
              <MainContainer />
            </ErrorBoundary>
          </section>
        </>
      ) : (
        <div
          className="d-flex justify-content-center align-items-center"
          style={{ height: '100vh' }}
        >
          <AppNoInfo />
        </div>
      )}
    </div>
  );
};

export default Layout;
